// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import getInvoicePdf from "@eisinc/App/Handlers/GetInvoicePdf";
import { actionOverflowMenu } from "@eisinc/App/SharedStyles/GeneralStyles";
import { iconsSharedStyles } from "@eisinc/App/SharedStyles/IconsStyles";
import { ButtonPresentationProps } from "@eisinc/MobiusOverrides/Button";
import { OverflowMenuProps } from "@eisinc/MobiusOverrides/OverflowMenu";
import Invoices2 from "@eisinc/XDL/Icons/Invoices2";
import Print from "@eisinc/XDL/Icons/Print";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { InvoiceStateContext } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { ShareEntityButtonStyles } from "@insite/content-library/Components/ShareEntityButton";
import Clickable, { ClickableProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Modal from "@insite/mobius/Modal";
import OverflowMenu from "@insite/mobius/OverflowMenu/OverflowMenu";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
const mapStateToProps = (state: ApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings,
});

const mapDispatchToProps = {
    getInvoicePdf,
};
// </XNG-Changes>
const enum fields {
    generateEmailAttachmentFromWebpage = "generateEmailAttachmentFromWebpage",
}

export interface InvoiceDetailHeaderStyles {
    shareEntityButtonStyles?: ShareEntityButtonStyles;
    buttonsHiddenContainer?: HiddenProps;
    menuHiddenContainer?: HiddenProps;
    headerGridContainer?: GridContainerProps;
    narrowOverflowMenu?: OverflowMenuProps;
    title: TypographyProps;
    titleGridItem: GridItemProps;
    printButton?: ButtonPresentationProps;
    buttonGridItem?: GridItemProps;
    printClickable?: ClickableProps;
    emailClickable?: ClickableProps;
    // XNG-Changes
    subTitle?: TypographyProps;
    pdfButton?: ButtonPresentationProps;
    buttonWrapper?: InjectableCss;
    easyPayButton?: LinkPresentationProps;
    easyPayButtonWithinOverflowMenu?: LinkPresentationProps;
    iconStyle?: IconPresentationProps;
    spinnerWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    modalPDFWrapper?: InjectableCss;
}
// XNG-Changes
interface InvoicesParam {
    invoicePdf: string;
}

export const headerStyles: InvoiceDetailHeaderStyles = {
    shareEntityButtonStyles: {
        button: {
            buttonType: "outline",
            variant: "secondary",
        },
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
        css: css`
            margin-left: 10px;
        `,
    },
    buttonGridItem: {
        css: css`
            justify-content: flex-end;
        `,
        width: [1, 1, 1, 6, 6],
    },
    titleGridItem: {
        width: [11, 11, 11, 6, 6],
    },
    title: {
        variant: "h2",
        as: "h1",
    },
    buttonsHiddenContainer: {
        below: "lg",
    },
    menuHiddenContainer: {
        above: "md",
    },
};

const styles = headerStyles;

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const InvoiceDetailHeader: FC<Props> = ({ fields, customSettings, getInvoicePdf }) => {
    const { value: invoice } = useContext(InvoiceStateContext);
    const easyPayUrl = (customSettings as any).easyPayUrl;
    const toasterContext = React.useContext(ToasterContext);
    const [isLoading, setIsLoading] = React.useState(false);
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [invoices, setInvoices] = React.useState([""]);

    if (!invoice) {
        return null;
    }

    const handlePDFClick = () => {
        setIsLoading(true);
        getInvoicePdf({
            invoiceNumber: invoice.invoiceNumber,
            onSuccess: (response: any) => {
                setInvoices(response.invoices);
                setIsLoading(false);

                if (response.invoices.length > 1) {
                    setIsModalOpen(true);
                } else {
                    handlePDFDownload(invoice.invoiceNumber, response?.invoices?.[0]);
                }
            },
            onError: (error: any) => {
                toasterContext.addToast({ body: translate("No Available PDF file"), messageType: "danger" });
                setIsLoading(false);
            },
        });
    };

    const handlePDFDownload = (invoiceNumber: string, invoice?: InvoicesParam) => {
        const pdfData = invoice?.invoicePdf;

        if (!pdfData) {
            return;
        }
        const fileName = `invoice ${invoiceNumber}.pdf`;
        const dataUri = `data:application/pdf;base64,${pdfData}`;
        const link = document.createElement("a");
        link.href = dataUri;
        link.target = "_blank";
        link.download = fileName;

        document.body.appendChild(link);

        const clickEvent = new MouseEvent("click", {
            view: window,
            bubbles: true,
            cancelable: false,
        });
        link.dispatchEvent(clickEvent);

        document.body.removeChild(link);
    };

    const modalCloseHandler = () => {
        setIsModalOpen(false);
    };

    const isCreditCard = invoice.properties.isCreditCard && invoice.properties.isCreditCard === "true";

    return (
        <>
            <GridContainer>
                <GridItem {...styles.titleGridItem}>
                    <Typography {...styles.title}>
                        {`${isCreditCard ? translate("Receipt") : translate("Invoice #")} ${invoice.invoiceNumber}`}
                    </Typography>
                    {invoice.properties.einNumber && (
                        <Typography {...styles.subTitle}>
                            {translate("EIN #")} {invoice.properties.einNumber}
                        </Typography>
                    )}
                </GridItem>
                <GridItem {...styles.buttonGridItem}>
                    <Hidden {...styles.menuHiddenContainer}>
                        <OverflowMenu hasChildPortal position="end" {...actionOverflowMenu}>
                            {isLoading && (
                                <StyledWrapper {...styles.spinnerWrapper}>
                                    <LoadingSpinner {...styles.spinner} />
                                </StyledWrapper>
                            )}
                            {!isLoading && (
                                <Clickable onClick={handlePDFClick} data-test-selector="invoiceDetails_PDF">
                                    {translate("PDF")}
                                </Clickable>
                            )}
                            <Link href="#" {...styles.easyPayButtonWithinOverflowMenu}>
                                {translate("Easy Pay")}
                            </Link>
                        </OverflowMenu>
                    </Hidden>
                    <Hidden {...styles.buttonsHiddenContainer}>
                        <StyledWrapper {...styles.buttonWrapper}>
                            {isLoading && <LoadingSpinner {...styles.spinner} />}
                            {!isLoading && (
                                <Clickable
                                    {...styles.pdfButton}
                                    onClick={handlePDFClick}
                                    data-test-selector="invoiceDetails_PDF"
                                >
                                    <Icon src={Print} {...styles.iconStyle} />
                                    {translate("PDF")}
                                </Clickable>
                            )}
                        </StyledWrapper>
                        {easyPayUrl && (
                            <StyledWrapper {...styles.buttonWrapper}>
                                <Link href={easyPayUrl} {...styles.easyPayButton}>
                                    <Icon
                                        src={Invoices2}
                                        size={16}
                                        color="primary.main"
                                        css={css`
                                            margin-right: 3px;
                                        `}
                                    />
                                    {translate("Easy Pay")}
                                </Link>
                            </StyledWrapper>
                        )}
                    </Hidden>
                </GridItem>
            </GridContainer>
            <Modal
                headline={siteMessage("Custom_InvoicesPDF_Modal_Title", "Invoices PDF")}
                size={200}
                isOpen={isModalOpen}
                handleClose={modalCloseHandler}
            >
                {invoices.map((invoiceFile: any, index) => (
                    <StyledWrapper key={index} {...styles.modalPDFWrapper}>
                        <Link
                            as="a"
                            href={`data:application/pdf;base64,${invoiceFile.invoicePdf}`}
                            download={`invoice ${invoice.invoiceNumber}_${index + 1}.pdf`}
                            onClick={modalCloseHandler}
                        >
                            {translate(`invoice ${invoice.invoiceNumber}_${index + 1}.pdf`)}
                        </Link>
                    </StyledWrapper>
                ))}
            </Modal>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceDetailHeader),
    definition: {
        displayName: "Page Header",
        allowedContexts: ["InvoiceDetailsPage"],
        group: "Invoice Details",
        fieldDefinitions: [
            {
                name: fields.generateEmailAttachmentFromWebpage,
                fieldType: "General",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                tooltip:
                    "If checked, sharing the invoice generates an email attachment using the Invoice Details page, rather than a pre-defined PDF file.",
            },
        ],
    },
};

export default widgetModule;
